import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { DataGrid, Column } from '@/components/DataGrid';
import { useEngagements, useLookup } from '@/api/hooks';
import { encodeEngId } from '@/lib/engId';

type Row = Record<string, unknown>;

const columns: Column<Row>[] = [
  { key: 'Source', header: 'Source' },
  { key: 'EngagementID', header: 'Eng ID' },
  { key: 'ServiceCategory', header: 'Service Category' },
  { key: 'TemplateName', header: 'Template Name' },
  { key: 'Year', header: 'Year' },
  { key: 'ClientName', header: 'Client Name' },
  { key: 'ClientManager', header: 'Client Manager' },
  { key: 'BRCIndustry', header: 'BRC Industry' },
  { key: 'EngagementManager', header: 'Engagement Manager' },
  { key: 'PracticeAreaPartner', header: 'Practice Area Partner' },
  { key: 'RequestStatus', header: 'Status' },
];

export function Engagements() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [visible, setVisible] = useState(50);
  const q = useEngagements(undefined, search || undefined, year || undefined);
  const years = useLookup('fiscal-years');
  const rows = q.data ?? [];
  const shown = rows.slice(0, visible);
  // SS11: new result set -> back to the first page.
  useEffect(() => { setVisible(50); }, [search, year]);

  return (
    // Full-height flex column so the grid scrolls inside a bounded area and its
    // horizontal scrollbar stays on screen (matches View / Change Clients).
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        {/* Year sits left of Search (Tyler, SS11 feedback). */}
        <div>
          <label className="label">Year</label>
          <select className="input w-32" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">All</option>
            {(years.data ?? []).map((y, i) => (
              <option key={i} value={String((y as Row).FiscalYear ?? '')}>{String((y as Row).FiscalYear ?? '')}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="label">Search (Client, Engagement ID, Template or Service)</label>
          <div className="flex gap-2">
            <input className="input" placeholder="e.g. Smith, 300953, Individual Tax, Assurance…" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)} />
            <button className="btn-secondary" onClick={() => setSearch(searchInput)}>Search</button>
          </div>
        </div>
        {(search || year) && (
          <button className="btn-secondary" onClick={() => { setSearch(''); setSearchInput(''); setYear(''); }}>
            Clear
          </button>
        )}
      </div>

      {/* The grid is the only scrolling area (no second scrollbar around it). */}
      <div className="min-h-0 flex-1">
        <DataGrid
          columns={columns}
          rows={shown}
          loading={q.isFetching}
          empty="No engagements match."
          rowAction={(row) => (
            <button title="View / Edit" className="text-primary hover:opacity-70"
              onClick={() => navigate(`/engagements/${encodeEngId(String(row.EngagementID))}`)}>
              <Eye className="h-4 w-4" />
            </button>
          )}
        />
      </div>
      <div className="flex flex-shrink-0 items-center gap-3 text-xs text-muted-foreground">
        <span>Showing {shown.length} of {rows.length}{rows.length >= 500 ? '+ (first 500)' : ''}. Use Search or Year to narrow.</span>
        {rows.length > visible && (
          <button className="btn-secondary !py-1 text-xs" onClick={() => setVisible((v) => v + 50)}>See More</button>
        )}
      </div>
    </div>
  );
}
